import type { Ref, SVGProps } from 'react'

/**
 * Icon Components
 *
 * Self-contained inline SVG wrappers (no runtime dependency on @central-icons-react),
 * one module per icon in ./icons/*.gen.tsx, deep-imported so a consumer's module
 * graph only ever contains the icons it uses.
 * Available sizes: Small (12px), Medium (14px), Large (16px)
 *
 * @example
 * import { IconFilter2 } from '#components/icon/IconFilter2'
 * import { IconSize } from '#components/icon/types'
 *
 * <IconFilter2 size={IconSize.Medium} />
 * <IconFilter2 size={IconSize.Small} className="text-green-500" />
 *
 * // For icons with fill option:
 * import { IconHeart } from '#components/icon/IconHeart'
 * import { IconFill } from '#components/icon/types'
 * <IconHeart size={IconSize.Medium} fill={IconFill.Filled} />
 *
 * @generate
 * To add new icons, run from packages/ui-design-system:
 *
 *   node scripts/generate-icons/index.mjs IconName1 IconName2 ...
 *   node scripts/generate-icons/index.mjs --filled IconName1 IconName2 ...
 *   node scripts/generate-icons/index.mjs --heavy IconName1 IconName2 ...
 *
 * --heavy uses the stroke-2 source packages and appends "Heavy" to the
 * generated component name (e.g. IconShieldCheckHeavy).
 *
 * New icons are picked up automatically by the `./icon/*` subpath pattern export —
 * there is no barrel to regenerate.
 * Icon names must match Central Icons names (see icon-names.md for full list)
 */

/**
 * Icon size variants
 */
export const IconSize = {
  Small: 'sm',
  Medium: 'md',
  Large: 'lg',
} as const

export type IconSize = (typeof IconSize)[keyof typeof IconSize]

/**
 * Size to pixel mapping
 */
export const iconSizeValues: Record<IconSize, number> = {
  [IconSize.Small]: 12,
  [IconSize.Medium]: 14,
  [IconSize.Large]: 16,
}

/**
 * Icon fill style
 */
export const IconFill = {
  Filled: 'filled',
  Outlined: 'outlined',
} as const

export type IconFill = (typeof IconFill)[keyof typeof IconFill]

/**
 * Base props for outlined-only icons
 */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'size'> {
  size?: IconSize
  /** Accessible title - when provided, icon is not hidden from screen readers */
  title?: string
  className?: string
  ref?: Ref<SVGSVGElement>
}

/**
 * Props for icons with outlined/filled variants.
 * `fill` here controls the icon style (outlined vs filled), not the SVG fill color.
 * The SVG always uses `currentColor` via CSS color inheritance.
 */
export interface IconWithFillProps extends IconProps {
  fill?: IconFill
}
